<template>
  <EBaseResult
    :title="title"
    :url="url"
    :source="source"
    class="web-result"
    data-test-id="organic-result"
    @title-click="onTitleClick"
  >
    <template #info-start>
      <EFavicon v-if="hasFavicon" class="web-result__favicon" :url="url" />
    </template>
    <template #info-end>
      <EDomainLabel
        class="web-result__domain-label"
        :domain-highlighting="domainHighlighting"
        :trigger-url="domainLabelTriggerUrl"
      />
    </template>
    <template #description>
      <p
        class="web-result__description"
        data-test-id="web-result-description"
      >
        {{ description }}
      </p>
    </template>
    <EDeepLinks
      v-if="deepLinksCapped.length > 0"
      :links="deepLinksCapped"
      :page-position="pagePosition"
      :rank="rank"
      type="web"
      data-test-id="web-result-deep-links"
    />
  </EBaseResult>
</template>

<script>
import { trackClick, TYPE_WEB } from '~~/search/common/js/client/analytics/results';
import { RESULT_DEEP_LINKS_TOTAL } from '~~/search/common/js/constants';
import EBaseResult, { baseProps } from '../base';
import EDeepLinks from '../add-ons/deep-links';
import EDomainLabel, {
  FOSSIL_FUEL,
  FOSSIL_FUEL_TRACKING,
  GREEN_LEAF,
  GREEN_LEAF_TRACKING,
  NONE,
} from '../add-ons/domain-label';

// @TODO remove after experiment is over
import EFavicon from './favicon';

export default {
  name: 'EWebResult',
  components: {
    EBaseResult,
    EDeepLinks,
    EDomainLabel,
    EFavicon,
  },
  props: {
    ...baseProps,
    deepLinks: {
      type: Array,
      default: () => [],
    },
    description: {
      type: String,
      required: true,
    },
    domainHighlighting: {
      type: Object,
      default: null,
    },
  },
  computed: {
    deepLinksCapped() {
      return this.deepLinks.slice(0, RESULT_DEEP_LINKS_TOTAL);
    },
    domainLabel() {
      return this.domain_highlighting ? this.domainHighlighting.ecosiaLabel : NONE;
    },
    trackingDomainLabel() {
      if (this.domainLabel === FOSSIL_FUEL) {
        return FOSSIL_FUEL_TRACKING;
      } if (this.domainLabel === GREEN_LEAF) {
        return GREEN_LEAF_TRACKING;
      }
      return false;
    },
    // TODO: enable when favicon proxy endpoint is ready
    hasFavicon() {
      return false;
    },
    domainLabelTriggerUrl() {
      return this.domainLabel !== NONE ? this.$props.url : '';
    },
    trackingOptions() {
      return {
        label: this.trackingDomainLabel || '',
        position: this.pagePosition,
        rank: this.rank,
        title: this.title,
        type: TYPE_WEB,
        url: this.source,
      };
    },
  },
  methods: {
    onTitleClick() {
      trackClick(this.trackingOptions);
    },
  },
};
</script>

<style lang="scss" scoped>
.web-result {
  position: relative;
}

.web-result__description {
  @include line-clamp(3);

  margin: 0;
}

.web-result__domain-label {
  @include desktop-down {
    position: absolute;
    top: -$space-2s;
    // larger offset to move domain label out of .result__info
    right: -$space-1l;
  }

  // .web-result for extra specificity
  .web-result &::before {
    content: none;
  }
}
</style>

<style lang="scss">
// Bing doesn't let us specify a different "hitHighlight" setting
// for Ad and Web Results, so we either get the `<b>`s always or never.
// To correct for this, we disable the bold styling of `<b>` tags just for WebResult.
.result__title,
.result__source,
.web-result__description {
  b {
    font-weight: inherit;
  }
}

.web-result__favicon {
  margin-top: - $space-2s; // So it sits visually more nicely on the URL baseline.
  margin-right: $space-1s;
}

.result__source--top .web-result__favicon {
  margin-top: -1px; // So it sits visually more nicely on the URL baseline (smaller font).
}

// Least intrusive solution to remove the dot separator.
// Cleanup the separator logic after favicon experiments.
.web-result__favicon + * {
  &::before {
    display: none;
  }
}
</style>
